import React, { useState, useEffect } from 'react';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { mockTimeSlots } from '../mock';
import './BookingPage.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: ''
  });

  const sessionOptions = [
    { id: 1, title: "1-on-1 Private Session", price: 450 },
    { id: 2, title: "2-on-1 Private Session", price: 350, note: "per participant" },
    { id: 3, title: "3-on-1 Private Session", price: 300, note: "per participant" },
    { id: 4, title: "Semi-Private Small Groups", price: 250, note: "per person" }
  ];

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedProgram || !selectedTime || !formData.name || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      // Prepare booking data
      const bookingData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        session_type: selectedProgram,
        session_date: selectedDate.toISOString(),
        session_time: selectedTime,
        notes: formData.notes || ''
      };

      // Make API call to backend
      const response = await axios.post(`${API}/bookings`, bookingData);

      if (response.data) {
        toast.success('Booking submitted successfully! We\'ll contact you within 24 hours to confirm.');
        
        // Reset form
        setSelectedDate(null);
        setSelectedProgram('');
        setSelectedTime('');
        setFormData({
          name: '',
          email: '',
          phone: '',
          notes: ''
        });
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit booking. Please try again.');
    }
  };

  return (
    <div className="booking-page">
      <Navbar />
      
      <div className="page-hero">
        <h1>BOOK YOUR SESSION</h1>
        <p>Choose your program, select a date and time, and get started</p>
      </div>

      <section className="section">
        <div className="booking-container">
          <div className="booking-form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h3>Select Program</h3>
                <Select value={selectedProgram} onValueChange={setSelectedProgram}>
                  <SelectTrigger className="select-input">
                    <SelectValue placeholder="Choose a training session" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionOptions.map((session) => (
                      <SelectItem key={session.id} value={session.id.toString()}>
                        {session.title} - R{session.price}{session.note ? ` (${session.note})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-section">
                <h3>Select Date</h3>
                <div className="calendar-wrapper">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => date < new Date()}
                    className="booking-calendar"
                  />
                </div>
              </div>

              <div className="form-section">
                <h3>Select Time</h3>
                <Select value={selectedTime} onValueChange={setSelectedTime}>
                  <SelectTrigger className="select-input">
                    <SelectValue placeholder="Choose a time slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {mockTimeSlots.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-section">
                <h3>Your Information</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-group full-width">
                    <Label htmlFor="notes">Additional Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows={4}
                      className="form-textarea"
                      placeholder="Tell us about your experience level, goals, or any special requirements..."
                    />
                  </div>
                </div>
              </div>

              <button type="submit" className="btn-primary full-width">
                Submit Booking Request
              </button>

              <p className="booking-note">
                * We'll contact you within 24 hours to confirm your booking and discuss any details.
              </p>
            </form>
          </div>

          <div className="booking-info">
            <div className="info-card">
              <h3>What Happens Next?</h3>
              <ol>
                <li>Submit your booking request</li>
                <li>We'll review your information</li>
                <li>Receive confirmation email within 24 hours</li>
                <li>Show up ready to train!</li>
              </ol>
            </div>

            <div className="info-card">
              <h3>Cancellation Policy</h3>
              <p>
                We understand plans change. Cancel up to 24 hours before your session for a full refund. 
                Cancellations within 24 hours may be subject to a fee.
              </p>
            </div>

            <div className="info-card">
              <h3>What to Bring</h3>
              <ul>
                <li>Athletic clothing</li>
                <li>Water bottle</li>
                <li>Hand wraps (or we can provide)</li>
                <li>Positive attitude and determination</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
};

export default BookingPage;